import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { baseChart, tightGrid } from '../ChartFrame'
import { LiveChartFrame } from './LiveChartFrame'
import { CardShell } from './CardShell'
import { DailyResponse, Resource } from './types'
import { ChartKind } from './sections'
import { SERIES, themed, useChartTheme } from './chartTheme'
import { tooltipHtml } from './tooltip'
import { UNIT, compact } from './format'
import { useFormat, useT } from './i18n'
import { dayTypeLabel } from './DailyConsumptionCard'

type Props = { daily: Resource<DailyResponse>; chartType: ChartKind }

const apexType = (kind: ChartKind): 'line' | 'area' | 'bar' => kind

const range12 = Array.from({ length: 12 }, (_, i) => i + 1)

export function SeasonalityCard({ daily, chartType }: Props) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.SECTION.SEASONALITY')}
      subtitle={t('PROFILES_DASHBOARD.SEASONALITY.SUBTITLE')}
      state={daily}
      empty={(d) => (d.kpis.complete_days ? null : t('PROFILES_DASHBOARD.COMMON.NO_COMPLETE_DAYS'))}
      render={(d) => <SeasonalityChart data={d} chartType={chartType} />}
    />
  )
}

function SeasonalityChart({ data, chartType }: { data: DailyResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const months = data.seasonality
  const options: ApexOptions = useMemo(
    () =>
      themed(
        {
          chart: { ...baseChart, type: apexType(chartType), toolbar: { show: false } },
          grid: tightGrid,
          // straight lines: smoothing would overshoot around months without data
          stroke: { curve: 'straight', width: chartType === 'bar' ? 0 : 3 },
          dataLabels: { enabled: false },
          xaxis: { categories: range12.map((m) => f.monthShort(m)) },
          yaxis: {
            title: { text: t('PROFILES_DASHBOARD.SEASONALITY.Y_AXIS') },
            min: 0,
            labels: { formatter: (v) => f.number(v, 2) },
          },
          colors: [SERIES.cyan],
          legend: { show: false },
          annotations: { yaxis: [{ y: 1, borderColor: theme.muted, strokeDashArray: 4 }] },
          tooltip: {
            shared: false,
            intersect: false,
            custom: ({ dataPointIndex }: any) => {
              const m = months[dataPointIndex]
              if (!m) return ''
              const title = f.monthLong(dataPointIndex + 1)
              if (m.index === null) {
                return tooltipHtml(
                  theme,
                  title,
                  [
                    {
                      label: t('PROFILES_DASHBOARD.COMMON.COMPLETE_DAYS'),
                      value: f.number(m.days),
                    },
                  ],
                  t('PROFILES_DASHBOARD.SEASONALITY.TIP_NOT_ENOUGH')
                )
              }
              return tooltipHtml(theme, title, [
                {
                  label: t('PROFILES_DASHBOARD.SEASONALITY.Y_AXIS'),
                  value: f.number(m.index, 2),
                  color: SERIES.cyan,
                },
                { label: t('PROFILES_DASHBOARD.COMMON.AVERAGE_DAY'), value: f.kwh(m.mean) },
                { label: t('PROFILES_DASHBOARD.COMMON.COMPLETE_DAYS'), value: f.number(m.days) },
              ])
            },
          },
        },
        theme
      ),
    [months, chartType, theme, t, f]
  )
  return (
    <>
      <LiveChartFrame>
        <ReactApexChart
          key={`season-${data.meta.start}-${data.meta.end}-${chartType}-${theme.mode}-${f.locale}`}
          options={options}
          series={
            [
              {
                name: t('PROFILES_DASHBOARD.SEASONALITY.SERIES'),
                data: months.map((m) => m.index),
              },
            ] as ApexAxisChartSeries
          }
          type={apexType(chartType)}
          width='100%'
          height={300}
        />
      </LiveChartFrame>
      {months.some((m) => m.days > 0 && m.index === null) ? (
        <div className='fs-8 text-muted'>{t('PROFILES_DASHBOARD.SEASONALITY.NOTE_BLANK')}</div>
      ) : null}
    </>
  )
}

export function OperationalCard({ daily, chartType }: Props) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.SECTION.OPERATIONAL')}
      subtitle={t('PROFILES_DASHBOARD.OPERATIONAL.SUBTITLE', { unit: UNIT })}
      state={daily}
      empty={(d) => (d.kpis.complete_days ? null : t('PROFILES_DASHBOARD.COMMON.NO_COMPLETE_DAYS'))}
      render={(d) => <OperationalChart data={d} chartType={chartType} />}
    />
  )
}

function OperationalChart({ data, chartType }: { data: DailyResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const rows = data.operational
  const options: ApexOptions = useMemo(
    () =>
      themed(
        {
          chart: { ...baseChart, type: apexType(chartType), toolbar: { show: false } },
          grid: { ...tightGrid, padding: { left: 8, right: 16, top: 8, bottom: 0 } },
          plotOptions: {
            bar: { horizontal: chartType === 'bar', borderRadius: 4, barHeight: '70%' },
          },
          // white text sits on purple bars, which is readable in both themes
          dataLabels: {
            enabled: chartType === 'bar',
            style: { colors: ['#FFFFFF'] },
            formatter: (v) => (v === null ? '' : compact(Number(v))),
          },
          xaxis: {
            categories: rows.map((r) =>
              t('PROFILES_DASHBOARD.OPERATIONAL.LABEL', {
                type: dayTypeLabel(t, r.key),
                count: r.days,
              })
            ),
            ...(chartType === 'bar'
              ? { labels: { formatter: (v: string) => compact(Number(v)) } }
              : {}),
          },
          yaxis:
            chartType === 'bar'
              ? { labels: { maxWidth: 230, style: { colors: theme.muted } } }
              : { labels: { formatter: compact } },
          colors: [SERIES.purple],
          legend: { show: false },
          stroke: { curve: 'straight', width: chartType === 'bar' ? 0 : 2 },
          tooltip: {
            shared: false,
            intersect: false,
            custom: ({ dataPointIndex }: any) => {
              const r = rows[dataPointIndex]
              if (!r) return ''
              return tooltipHtml(theme, dayTypeLabel(t, r.key), [
                {
                  label: t('PROFILES_DASHBOARD.COMMON.AVERAGE_DAY'),
                  value: f.kwh(r.mean),
                  color: SERIES.purple,
                },
                { label: t('PROFILES_DASHBOARD.COMMON.DAYS'), value: f.number(r.days) },
              ])
            },
          },
        },
        theme
      ),
    [rows, chartType, theme, t, f]
  )
  return (
    <LiveChartFrame>
      <ReactApexChart
        key={`ops-${data.meta.start}-${data.meta.end}-${chartType}-${theme.mode}-${f.locale}`}
        options={options}
        series={
          [
            {
              name: t('PROFILES_DASHBOARD.OPERATIONAL.SERIES', { unit: UNIT }),
              data: rows.map((r) => r.mean),
            },
          ] as ApexAxisChartSeries
        }
        type={apexType(chartType)}
        width='100%'
        height={280}
      />
    </LiveChartFrame>
  )
}

export function YearlyCard({ daily, chartType }: Props) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.SECTION.YEARLY')}
      subtitle={t('PROFILES_DASHBOARD.YEARLY.SUBTITLE', { unit: UNIT })}
      state={daily}
      empty={(d) => (d.kpis.complete_days ? null : t('PROFILES_DASHBOARD.COMMON.NO_COMPLETE_DAYS'))}
      render={(d) => <YearlyChart data={d} chartType={chartType} />}
    />
  )
}

function YearlyChart({ data, chartType }: { data: DailyResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const rows = data.yearly
  const options: ApexOptions = useMemo(() => {
    const opts: ApexOptions = {
      chart: { ...baseChart, type: apexType(chartType), toolbar: { show: false } },
      grid: tightGrid,
      stroke: { curve: 'straight', width: chartType === 'bar' ? 0 : 2 },
      dataLabels: { enabled: false },
      xaxis: { categories: rows.map((y) => (y.partial ? `${y.year}*` : String(y.year))) },
      yaxis: {
        title: { text: t('PROFILES_DASHBOARD.DAILY.Y_AXIS', { unit: UNIT }) },
        labels: { formatter: compact },
        min: 0,
      },
      colors: [SERIES.cyan],
      legend: { show: false },
      tooltip: {
        shared: false,
        intersect: false,
        custom: ({ dataPointIndex }: any) => {
          const y = rows[dataPointIndex]
          if (!y) return ''
          return tooltipHtml(
            theme,
            String(y.year),
            [
              {
                label: t('PROFILES_DASHBOARD.COMMON.AVERAGE_DAY'),
                value: f.kwh(y.mean),
                color: SERIES.cyan,
              },
              { label: t('PROFILES_DASHBOARD.COMMON.COMPLETE_DAYS'), value: f.number(y.days) },
              {
                label: t('PROFILES_DASHBOARD.COMMON.COVERAGE'),
                value: `${f.number(Math.round(y.coverage * 100))}%`,
              },
            ],
            y.partial ? t('PROFILES_DASHBOARD.YEARLY.TIP_PARTIAL') : undefined
          )
        },
      },
    }
    // Never pass `fill: undefined`: ApexCharts would overwrite its default fill settings with it
    // and crash ("Cannot read properties of undefined (reading 'colors')").
    if (chartType === 'area') {
      opts.fill = { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } }
    }
    return themed(opts, theme)
  }, [rows, chartType, theme, t, f])
  return (
    <>
      <LiveChartFrame>
        <ReactApexChart
          key={`yearly-${data.meta.start}-${data.meta.end}-${chartType}-${theme.mode}-${f.locale}`}
          options={options}
          series={[
            {
              name: t('PROFILES_DASHBOARD.OPERATIONAL.SERIES', { unit: UNIT }),
              data: rows.map((y) => y.mean),
            },
          ]}
          type={apexType(chartType)}
          width='100%'
          height={280}
        />
      </LiveChartFrame>
      {rows.some((y) => y.partial) ? (
        <div className='fs-8 text-muted'>{t('PROFILES_DASHBOARD.YEARLY.FOOTNOTE')}</div>
      ) : null}
    </>
  )
}
